import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { DateTime } from 'luxon';
import cx from 'classnames';
import PropTypes from 'prop-types';

import withStyles from '@mui/styles/withStyles';
import Grid from '@mui/material/Grid';

import { updatePerson } from '../store/slices/personsSlice';
import { addStatusTransitions } from '../store/slices/statusTransitionsSlice';
import { clearTaskAccomplishmentInfo } from '../store/slices/dashboardStatsSlice';
import { setIntendedReferrerContactId, setIntendedReferralId } from '../store/slices/intendedInfoSlice';
import { addEmrClient, setEmrClientFacility } from '../store/slices/emrInfoSlice';
import {
  updateCase,
  changeCurrentCaseAdmissionDate,
  changeCurrentCaseLeadTemperature,
  changeCurrentCaseSalesStage,
} from '../store/slices/casesSlice';
import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import {
  createStdRequest,
  apiFetch,
  validatePhoneNumber,
  validateEmail,
  isNonLocalNonLoggedInVisitor,
  sortedDropdownItemLabels,
  reportTime,
  validateAddress,
  showAlert,
  logWithTime,
  defaultBirthdayKludge,
} from '../utils/utils';

import {
  outreachRepForDisplay,
  transitionRepForDisplayAsActivity,
  noteRepForDisplayAsActivity,
  sortIntoActivityGroups,
} from '../utils/activityProcessor';

import SocketAccess from '../comm/SocketAccess';

import ReduxLoader from '../store/ReduxLoader';
import DA from '../store/ReduxAccess';

import VobModal from '../components/VobModal';
import ActivitySummaryBox from '../components/ActivitySummaryBox';
import ActionPanel from '../components/ActionPanel';
import ActivityList from '../components/ActivityList';
import ContactAssocGroup from '../components/ContactAssocGroup';
import NoteAssocGroup from '../components/NoteAssocGroup';
import TaskAssocGroup from '../components/TaskAssocGroup';
import DocsAssocGroup from '../components/DocsAssocGroup';
import ClickAwayTextField from '../components/ClickAwayTextField';
import FacePageUsAddressPanel from '../components/FacePageUsAddressPanel';
import KSelect from '../components/KSelect';
import CondensedOrgModal from '../components/CondensedOrgModal';
import KSpinner from '../components/KSpinner';
import CrmButton from '../components/CrmButton';
import AlertModal from '../components/AlertModal';
import CrmSnackbar from '../components/CrmSnackbar';
import KDatePicker from '../components/KDatePicker';
import ReferralModal from '../components/ReferralModal';

import facePageStyles from '../assets/jss/crm/views/facePageStyles2';

const casePageStyles = (theme) => ({
  ...facePageStyles(theme),
  referrerDiv: {
    marginLeft: '12px',
  },
  labeledDropdown: {
    marginLeft: '12px',
  },
  addReferralInfoLink: {
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
});

class CasePage extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      shouldShowVobForm: false,
      shouldShowNewReferralDialog: false,
      client: {
        personId: 0,
        name: '',
        description: '',
        phoneNumbers: {
          number: '',
          roles: '',
        },
        email: null,
        address: {
          street: '',
          city: '',
          state: '',
          zip: '',
        },
        status: '',
        sex: '',
        birthDate: null,
        ownerId: 0,
        facilityName: '',
      },
      activityGroups: undefined,
      nOutreaches: 0,
      editingAddress: false,
      preEditAddress: undefined,
      showCondensedOrgModal: false,
      snackbar: {
        isOpen: false,
        message: '',
      },
      addressWasChangedExternally: false,
    };
  }

  tryUpdatingViaApi = false;

  newPhoneObject = null;
  refreshDeltaUpdaterIntervalId = 0;

  addressValidationPolicy = {
    requireAddress: false,
    forbidPartialAddress: true,
    allowZipFreeAddress: true,
  };

  componentDidMount() {
    this.loadRequiredData();
    this.registerRefreshCallbacks();
  }

  registerRefreshCallbacks() {
    const personCallbackStruct = {
      kind: 'person',
      pageName: 'CasePage',
      refreshFunc: () => this.redisplayPageForChangedPerson(),
    };
    SocketAccess.registerRefreshCallback(personCallbackStruct);

    const clientCallbackStruct = {
      kind: 'client',
      pageName: 'CasePage',
      refreshFunc: () => this.redisplayPageForChangedPerson(),
    };
    SocketAccess.registerRefreshCallback(clientCallbackStruct);

    const outreachCallbackStruct = {
      kind: 'outreach',
      pageName: 'CasePage',
      refreshFunc: () => this.redisplayPageForChangedOutreach(),
    };
    SocketAccess.registerRefreshCallback(outreachCallbackStruct);

    const statusCallbackStruct = {
      kind: 'statusTransition',
      pageName: 'CasePage',
      refreshFunc: () => this.redisplayPageForChangedStatus(),
    };
    SocketAccess.registerRefreshCallback(statusCallbackStruct);

    const referralCallbackStruct = {
      kind: 'referral',
      pageName: 'CasePage',
      refreshFunc: () => this.redisplayPageForChangedReferral(),
    };
    SocketAccess.registerRefreshCallback(referralCallbackStruct);
  }

  redisplayPageForChangedPerson() {
    const intendedClientId = DA.getCaseIntendedClientId();
    this.setStateClient(intendedClientId);
  }

  redisplayPageForChangedOutreach() {
    const clientId = DA.getCaseIntendedClientId();
    logWithTime('In CasePage calling buildActivityGroupsFromReduxStore() in hopes of showing new call');
    this.buildActivityGroupsFromReduxStore(clientId);
  }

  redisplayPageForChangedStatus() {
    const clientId = DA.getCaseIntendedClientId();
    this.buildActivityGroupsFromReduxStore(clientId);
    this.setStateClient(clientId);
  }

  redisplayPageForChangedReferral() {
    // does this do the job?
    const clientId = DA.getCaseIntendedClientId();
    this.setStateClient(clientId);
  }

  componentWillUnmount() {
    clearInterval(this.refreshDeltaUpdaterIntervalId);
    SocketAccess.unregisterRefreshCallbacksForPage('CasePage');
  }

  loadRequiredData() {
    // I think there may be five situations in which we arrive here.
    // 1) User typed in "case" in the URL input
    // 2) First time since browser refresh we've arrived at this page
    // 3) First time we've arrived for the intended client
    // Second or later time for the intended client
    //   4) w/ an intervening visit for some other client
    //   5) w/o an intervening visit from some other client
    const intendedClientId = DA.getCaseIntendedClientId();
    const currentCase = DA.getCurrentCase();
    if (currentCase && currentCase.clientId === intendedClientId) {
      // This is case (5) above.
      // We've previously stored the exact Redux state on which this page relies.
      // Trust it to be accurate, and don't re-fetch it all
      this.setTreatmentState();
      const continuation = () => {
        this.buildActivityGroupsFromReduxStore(intendedClientId);
        this.setState({ loading: false });
      };
      this.setStateClient(intendedClientId, continuation);
    } else {
      // We're in one of the cases (1) to (4)
      if (currentCase) {
        // We're in either case (3) or case (4)
        if (DA.haveLoadedCaseForClientWithId(intendedClientId)) {
          // We're in case (4), so we just need to make that case "current"
          DA.makeCurrentCaseForClientWithId(intendedClientId);
          this.setTreatmentState();
          const continuation = () => {
            this.buildActivityGroupsFromReduxStore(intendedClientId);
            this.setState({ loading: false });
          };
          this.setStateClient(intendedClientId, continuation);
        } else {
          // We're in case (3), so we need to load up the Redux store
          this.fetchCaseInfoAndDisplay(intendedClientId);
        }
      } else {
        // We're in either case (1) or case (2)
        // In case 1, we'd like to give the user a hard time
        // In case 2, we need to load up the Redux store with everything needed for this page
        if (intendedClientId === 0) {
          // We're in case (1), so give the user a hard time
          this.setState({ admonishUser: true });
        } else {
          // We're in case (2), so load up the Redux store
          this.fetchCaseInfoAndDisplay(intendedClientId);
        }
      }
    }
  }

  fetchCaseInfoAndDisplay(clientId) {
    const b4Fetch = DateTime.local();
    this.setState({ loading: true, loadProgress: 0 });
    const callback = () => {
      reportTime('loadCasePageInfoForClient', b4Fetch);
      const b4Other = DateTime.local();

      this.setStateClient(clientId);
      this.setTreatmentState();
      this.buildActivityGroupsFromReduxStore(clientId);
      this.setState({ loading: false, loadProgress: 0 });

      reportTime('other', b4Other);
      reportTime('fetchCaseInfoAndDisplay', b4Fetch);
    };
    ReduxLoader.loadCasePageInfoForClient(this, clientId, callback);
  }

  setStateClient(clientId, continuation) {
    const myClient = DA.getPersonById(clientId);
    const haveAddresses = myClient.addresses && myClient.addresses.length > 0;
    const address = haveAddresses
      ? {
          street: myClient.addresses[0].line1,
          city: myClient.addresses[0].city,
          state: myClient.addresses[0].state,
          zip: myClient.addresses[0].zip,
        }
      : {
          street: '',
          city: '',
          state: '',
          zip: '',
        };
    const description = myClient.description || '';
    const havePhone = myClient.phoneNumbers && myClient.phoneNumbers.length > 0;
    const phoneNumber = havePhone ? myClient.phoneNumbers[0].number : '';
    const email = myClient.email ? myClient.email : '';

    const emrClient = DA.getEmrProspectDetail(myClient.personId);
    const facilityName = emrClient ? emrClient.facilityName : '';

    const augmentedClient = {
      ...myClient,
      name: myClient.preferredFullName,
      email,
      address,
      phoneNumber,
      description,
      facilityName,
    };

    if (continuation) {
      const continuation1 = () => this.setState({ addressWasChangedExternally: false }, continuation);
      this.setState({ client: augmentedClient }, continuation1);
    } else {
      const continuation1 = () => this.setState({ addressWasChangedExternally: false });
      this.setState({ client: augmentedClient, addressWasChangedExternally: true }, continuation1);
    }
  }

  setTreatmentState() {
    // MKM do anything here?
  }

  buildActivityGroupsFromReduxStore(personId) {
    const outreaches = DA.getOutreachHistoryForClientWithId(personId);
    const augmentedOutreaches = outreaches.map((outreach) => outreachRepForDisplay(outreach));
    const viewingFirmId = DA.getFirmId();
    const statusTransitions = DA.getStatusHistorysForFirmIdAndClientId(viewingFirmId, personId);
    const augmentedTransitions = statusTransitions.map((trans) => transitionRepForDisplayAsActivity(trans));
    const notes = this.getNotesAboutClient(personId);
    const augmentedNotes = notes.map((note) => noteRepForDisplayAsActivity(note));
    const outreachesTransitionsAndNotes = augmentedOutreaches.concat(augmentedTransitions).concat(augmentedNotes);
    const activityGroups = sortIntoActivityGroups(outreachesTransitionsAndNotes);
    this.setState({ activityGroups, nOutreaches: augmentedOutreaches.length });
  }

  showOrgContactFaceSheet(subjectId) {
    showAlert(this, 'Should now show the face sheet for org contact with ID: ' + subjectId);
  }

  functionToShowSubject(activity) {
    switch (activity.subjectType) {
      case 'orgContact':
        return () => this.showOrgContactFaceSheet(activity.subjectId);
      case 'org':
        return () => showAlert(this, "You're already on the page for this organization");
      default:
        return () => showAlert(this, "Don't know how to show subject of type: " + activity.subjectType);
    }
  }

  getContactsForClient() {
    // TODO should probably do the viewingFirm-specific way
    // const viewingFirmId = DA.getFirmId();
    // return DA.getContactsForClient(viewingFirmId, this.state.client.personId);
    const contacts = DA.getContactsForClient(this.state.client);
    return contacts.map((c) => this.createAugmentedContactForDisplay(c));
  }

  getNotesAboutClient(clientId) {
    const client = DA.getPersonById(clientId);
    return DA.getNotesOnPerson(client);
  }

  getPossibleTaskSubjects() {
    return [this.state.client].concat(this.getContactsForClient());
  }

  getTaskSubjectCategory() {
    return 'client';
  }

  createAugmentedContactForDisplay(contact) {
    /* has only personId and relationship */
    const person = DA.getPersonById(contact.personId);
    const inital2 = person.lastName ? person.lastName[0] : '';
    const initials = person.preferredFullName[0] + inital2;
    const havePhone = person.phoneNumbers && person.phoneNumbers[0];
    const phone = havePhone ? person.phoneNumbers[0].number : '';
    return { ...person, initials, phone, relationship: contact.relationship };
  }

  handleClientNameChange(newValue) {
    const newClientState = { ...this.state.client, name: newValue };
    this.setState({ client: newClientState });
  }

  handleClientStatusChange(newValue) {
    const clientId = this.state.client.personId;
    const viewingFirmId = DA.getFirmId();
    const uri = '/api/status';
    const apiStruct = {
      firmId: viewingFirmId,
      clientId: clientId,
      date: DateTime.local(),
      newStatus: newValue,
    };
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // update Redux store to show this new state
      const reduxArray = ReduxLoader.convertStatusHistoryArrayApiStructIntoReduxStruct([json.transition]);
      this.props.addStatusTransitions(reduxArray);
      this.buildActivityGroupsFromReduxStore(clientId);
      const client = DA.getPersonById(clientId);
      const clientWithNewStatus = { ...client, status: newValue };
      this.props.updatePerson(clientWithNewStatus);
      // this.sendReduxUpdateForStatusTransitionToWebSocket(clientId, json.transition.statusTransitionId);
      this.setStateClient(clientId, () => this.setState({ loading: false }));
    };
    this.setState({ loading: true });
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleClientBirthDateChange(newDate) {
    const newClientState = { ...this.state.client, birthDate: newDate };
    this.setState({ client: newClientState }, () => this.saveBirthDateToDb());
  }

  saveBirthDateToDb() {
    this.saveClientStateFieldToDb('birthDate', 'Date of Birth');
  }

  handleClientSexChange(newSex) {
    const newClientState = { ...this.state.client, sex: newSex };
    this.setState({ client: newClientState }, () => this.saveSexToDb());
  }

  saveSexToDb() {
    this.saveClientStateFieldToDb('sex', 'Gender');
  }

  handleClientEmrFacilityChange(newFacilityName) {
    const newClientState = { ...this.state.client, facilityName: newFacilityName };
    this.setState({ client: newClientState });

    const clientApiStruct = this.buildClientApiStructForSyncFacility(newFacilityName);

    const uri = '/api/case/syncFacility';
    const request = createStdRequest(uri, 'POST', clientApiStruct);
    const actionOnGoodResponse = () => {
      this.showSnackbar('Facility');
      const emrClient = DA.getEmrProspectDetail(newClientState.personId);
      if (!emrClient) {
        // MKM: Remove diagnostic logging when we've fixed the 'disappearing facilities' issue
        console.log(`Didn't find emrClient with personId=${newClientState.personId}. This seems like a bug`);
        const newEmrClient = {
          personId: newClientState.personId,
          name: {
            first: newClientState.firstName,
            last: newClientState.lastName,
          },
          phone: {
            other: newClientState.phoneNumber,
          },
          facilityName: newFacilityName,
        };
        this.props.addEmrClient(newEmrClient);
      } else {
        // MKM: Remove diagnostic logging when we've fixed the 'disappearing facilities' issue
        console.log(`calling setEmrClientFacility({${newClientState.personId},${newFacilityName}})`);
        this.props.setEmrClientFacility({ personId: newClientState.personId, facilityName: newFacilityName });
      }
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildClientApiStructForSyncFacility(newFacilityName) {
    const currentCase = DA.getCurrentCase();
    const clientId = DA.getCaseIntendedClientId();
    return {
      caseId: currentCase.caseId,
      clientId,
      firmId: this.props.firmId,
      syncEmrFacility: newFacilityName,
    };
  }

  simpleGetDocsForClient() {
    const clientId = DA.getCaseIntendedClientId();
    const docs = DA.getDocumentsAboutPersonWithPersonId(clientId);
    const sortedDocs = docs.sort((a, b) => (a.publicName.toLowerCase() > b.publicName.toLowerCase() ? 1 : -1));
    return sortedDocs;
  }

  getClientPlusContacts() {
    const contacts = this.getContactsForClient();
    const clientAsContact = this.createAugmentedContactForDisplay(this.state.client);
    return [clientAsContact].concat(contacts);
  }

  handleClickOutsideName(newValue) {
    const changed = newValue !== this.state.client.name;
    if (changed) {
      const newClientState = { ...this.state.client, name: newValue };
      this.setState({ client: newClientState }, () => this.saveNameToDb());
    }
  }

  saveNameToDb() {
    this.saveClientStateFieldToDb('name', 'Name');
  }

  handleClickOutsidePhone(newValue) {
    const changed = newValue !== this.state.client.phoneNumber;
    if (changed) {
      const newClientState = { ...this.state.client, phoneNumber: newValue };
      this.setState({ client: newClientState }, () => this.savePhoneToDb());
    }
  }

  savePhoneToDb() {
    const proposedNumber = this.state.client.phoneNumber;
    if (proposedNumber) {
      const validation = validatePhoneNumber(proposedNumber);
      if (!validation.valid) {
        showAlert(this, validation.errorMessage);
        return;
      }
    }
    if (proposedNumber.length === 0) {
      this.newPhoneObject = {};
    } else {
      // We have a valid phone number and must either modify the previous one or add this new one
      const client = DA.getPersonById(this.state.client.personId);
      const clientHasPhone = client.phoneNumbers && client.phoneNumbers[0];
      if (clientHasPhone) {
        const primaryPhone = client.phoneNumbers[0];
        this.newPhoneObject = { ...primaryPhone, number: proposedNumber };
      } else {
        this.newPhoneObject = {
          roles: 'Mobile,Home',
          countryCode: '1',
          number: proposedNumber,
        };
      }
    }
    // now save this to the DB and update Redux
    this.saveClientStateFieldToDb('phoneNumber', 'Phone number');
  }

  handleClickOutsideEmail(newValue) {
    // const visibleValue = newValue === null ? '[null]' : newValue === '' ? '[empty]' : newValue;
    // console.log(`in handleClickOutsideEmail(${visibleValue})`);
    const trimmedValue = newValue.trim();
    // const visibleTrimmedValue = trimmedValue === null ? '[null]' : trimmedValue === '' ? '[empty]' : trimmedValue;
    const eTemp = this.state.client.email;
    // const visibleEmail = eTemp === null ? '[null]' : eTemp === '' ? '[empty]' : eTemp;
    const unchanged = trimmedValue == eTemp || (eTemp === null && trimmedValue == '');
    const changed = !unchanged;
    // console.log(`  email: ${visibleEmail}; trimmedValue: ${visibleTrimmedValue} ==> changed: ${changed}`);
    if (changed) {
      const newClientState = { ...this.state.client, email: trimmedValue || null };
      this.setState({ client: newClientState }, () => this.saveEmailToDb());
    }
  }

  saveEmailToDb() {
    const proposedEmail = this.state.client.email;
    /*
    let emailToShowInConsole = '';
    if (proposedEmail === '') {
      emailToShowInConsole = '[empty]';
    } else if (proposedEmail === null) {
      emailToShowInConsole = '[null]';
    } else {
      emailToShowInConsole = proposedEmail;
    }
    console.log(`in saveEmailToDb(); proposedEmail: ${emailToShowInConsole}`);
    */
    if (proposedEmail) {
      const validation = validateEmail(proposedEmail, proposedEmail + " doesn't look like a valid email address");
      if (!validation.valid) {
        showAlert(this, validation.errorMessage);
        return;
      } else {
        const newClientState = { ...this.state.client, email: proposedEmail };
        this.setState({ client: newClientState }, () => this.saveClientStateFieldToDb('email', 'Email'));
      }
    } else {
      // presumably, user is trying to delete email.  We'll allow that
      const newClientState = { ...this.state.client, email: null };
      this.setState({ client: newClientState }, () => this.saveClientStateFieldToDb('email', 'Email'));
    }
  }

  handleClickOutsideDescription(newValue) {
    const changed = newValue !== this.state.client.description;
    if (changed) {
      const newClientState = { ...this.state.client, description: newValue };
      this.setState({ client: newClientState }, () => this.saveDescriptionToDb());
    }
  }

  saveDescriptionToDb() {
    this.saveClientStateFieldToDb('description', 'Description');
  }

  saveDescriptionToDbViaPatch() {
    // build request body of "patch instructions"
    const personId = this.state.client.personId;
    const description = this.state.client.description;
    const instructions = [{ op: 'replace', path: '/description', value: description }];
    const uri = `/api/client/${personId}`;
    const request = createStdRequest(uri, 'PATCH', instructions);
    const actionOnGoodResponse = () => {
      // store revised state in Redux
      const reduxRep = DA.getPersonById(personId);
      const copiedRep = { ...reduxRep, description };
      this.props.updatePerson(copiedRep);

      this.showSnackbar('Description');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleAddressChange(newAddress) {
    const newClientState = { ...this.state.client, address: newAddress };
    this.setState({ client: newClientState }, () => this.validateAndSaveAddressToDb());
  }

  localValidateAddress(street, city, state, zip) {
    const { requireAddress, forbidPartialAddress, allowZipFreeAddress } = this.addressValidationPolicy;
    return validateAddress(street, '', city, state, zip, requireAddress, forbidPartialAddress, allowZipFreeAddress);
  }

  validateAndSaveAddressToDb() {
    const { street, city, state, zip } = this.state.client.address;
    const validation = this.localValidateAddress(street, city, state, zip);
    if (validation.valid) {
      this.saveClientStateFieldToDb('address', 'Address');
    } else {
      showAlert(this, validation.errorMessage);
    }
  }

  saveNameToDbViaPatch() {
    // build URI
    const personId = this.state.client.personId;
    const uri = `/api/client/${personId}`;

    // build request body of "patch instructions"
    const name = this.state.client.name.trim();
    const firstSpaceLoc = name.indexOf(' ');
    const lastSpaceLoc = name.lastIndexOf(' ');
    const firstName = name.substring(0, firstSpaceLoc);
    const lastName = lastSpaceLoc >= firstSpaceLoc ? name.substring(lastSpaceLoc + 1) : '';
    const middleNames = lastSpaceLoc > firstSpaceLoc ? name.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';

    const person = DA.getPersonById(personId);
    const oldMiddleNames = person.middleNames;
    const oldLastName = person.lastName;
    const oldPreferredName = person.preferredName;

    let instructions = [];
    if (firstName !== oldPreferredName) {
      instructions = [...instructions, { op: 'replace', path: '/firstName', value: firstName }];
      instructions = [...instructions, { op: 'replace', path: '/preferredName', value: firstName }];
    }
    if (middleNames !== oldMiddleNames) {
      instructions = [...instructions, { op: 'replace', path: '/middleNames', value: middleNames }];
    }
    if (lastName !== oldLastName) {
      instructions = [...instructions, { op: 'replace', path: '/lastName', value: lastName }];
    }

    const request = createStdRequest(uri, 'PATCH', instructions);
    const actionOnGoodResponse = () => {
      // store revised state in Redux
      const reduxRep = DA.getPersonById(personId);
      const pref = firstName;
      const prefFull = `${pref} ${lastName}`;
      const copiedRep = {
        ...reduxRep,
        firstName,
        middleNames,
        lastName,
        preferredName: pref,
        preferredFullName: prefFull,
      };
      this.props.updatePerson(copiedRep);

      this.showSnackbar('Name');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  savePhoneNumberToDbViaPatch() {
    // build URI
    const personId = this.state.client.personId;
    const uri = `/api/client/${personId}`;

    // build request body of "patch instructions"
    const value = this.newPhoneObject;
    const instructions = [{ op: 'replace', path: '/phoneNumbers/0', value }];

    const request = createStdRequest(uri, 'PATCH', instructions);
    const actionOnGoodResponse = () => {
      const reduxRep = DA.getPersonById(personId);
      const phoneNumbers = this.createPhonesArrayFromState();
      const copiedRep = { ...reduxRep, phoneNumbers };
      this.props.updatePerson(copiedRep);

      this.showSnackbar('Phone Number');
    };

    apiFetch(this, request, actionOnGoodResponse);
  }

  saveAddressToDbViaPatch() {
    // build URI
    const personId = this.state.client.personId;
    const uri = `/api/client/${personId}`;

    // build request body of "patch instructions"
    const { street, city, state, zip } = this.state.client.address;
    const value = {
      line1: street,
      city,
      state,
      zip,
    };
    const instructions = [{ op: 'replace', path: '/addresses/0', value }];

    const request = createStdRequest(uri, 'PATCH', instructions);
    const actionOnGoodResponse = () => {
      // store revised state in Redux
      const reduxRep = DA.getPersonById(personId);
      const addresses = this.createAddressesArrayFromState();
      const copiedRep = { ...reduxRep, addresses };
      this.props.updatePerson(copiedRep);

      this.showSnackbar('Address');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  saveSexToDbViaPatch() {
    // build URI
    const personId = this.state.client.personId;
    const uri = `/api/client/${personId}`;

    // build request body of "patch instructions"
    const value = this.state.client.sex;
    const instructions = [{ op: 'replace', path: '/sex', value }];

    const request = createStdRequest(uri, 'PATCH', instructions);

    const actionOnGoodResponse = () => {
      // store revised state in Redux
      const reduxRep = DA.getPersonById(personId);
      const copiedRep = { ...reduxRep, sex: value };
      this.props.updatePerson(copiedRep);

      this.showSnackbar('Sex');
    };

    apiFetch(this, request, actionOnGoodResponse);
  }

  saveBirthDateToDbViaPatch() {
    // build URI
    const personId = this.state.client.personId;
    const uri = `/api/client/${personId}`;

    // build request body of "patch instructions"
    const luxonBDay = this.state.client.birthDate;
    const value = luxonBDay.toISODate();
    const instructions = [{ op: 'replace', path: '/birthDate', value }];

    const request = createStdRequest(uri, 'PATCH', instructions);

    const actionOnGoodResponse = () => {
      // store revised state in Redux
      const reduxRep = DA.getPersonById(personId);
      const copiedRep = { ...reduxRep, birthDate: value };
      this.props.updatePerson(copiedRep);

      this.showSnackbar('Date of Birth');
    };

    apiFetch(this, request, actionOnGoodResponse);
  }

  shouldHandleFieldNameViaPatch(clientStateFieldName) {
    switch (clientStateFieldName) {
      case 'name':
      case 'phoneNumber':
      case 'address':
      case 'sex':
      case 'birthDate':
      case 'description':
        return true;
      default:
        return false;
    }
  }

  saveClientStateFieldToDbViaPatch(clientStateFieldName, message) {
    switch (clientStateFieldName) {
      case 'name':
        this.saveNameToDbViaPatch();
        break;
      case 'phoneNumber':
        this.savePhoneNumberToDbViaPatch();
        break;
      case 'address':
        this.saveAddressToDbViaPatch();
        break;
      case 'sex':
        this.saveSexToDbViaPatch();
        break;
      case 'birthDate':
        this.saveBirthDateToDbViaPatch();
        break;
      case 'description':
        this.saveDescriptionToDbViaPatch();
        break;
      default:
        return false;
    }
  }

  saveClientStateFieldToDb(clientStateFieldName, message) {
    if (this.shouldHandleFieldNameViaPatch(clientStateFieldName)) {
      return this.saveClientStateFieldToDbViaPatch(clientStateFieldName, message);
    }

    // build API struct
    const clientApiStruct = this.buildClientApiStructForUpdatingClient();
    const uri = '/api/client';
    const request = createStdRequest(uri, 'post', clientApiStruct);
    const actionOnGoodResponse = (json) => {
      // store revised state in Redux
      // MKM probably need to convert to Redux form first, or maybe just use the following shortcut:
      const personId = this.state.client.personId;
      const reduxRep = DA.getPersonById(personId);
      const reduxFieldNames = this.reduxFieldNamesForClientStateName(clientStateFieldName);
      let copiedRep = reduxRep;
      reduxFieldNames.forEach(
        (reduxFieldName) => (copiedRep = { ...copiedRep, [reduxFieldName]: clientApiStruct[reduxFieldName] })
      );
      /*
      if (clientStateFieldName == 'email') {
        const email = copiedRep.email;
        console.log(`Calling updatePerson() with email: ${copiedRep.email}`);
      }
      */
      this.props.updatePerson(copiedRep);

      this.showSnackbar(message);
    };

    /*
    if (clientStateFieldName == 'email') {
      const email = clientApiStruct.email;
      console.log(`Calling ${uri} with email: ${clientApiStruct.email}`);
    }
    */

    apiFetch(this, request, actionOnGoodResponse);
  }

  /*
   * Now doing this from the backend at the end of the API code
  sendReduxUpdateForStatusTransitionToWebSocket(personId, transitionId) {
    if (this.tryUpdatingViaApi) {
      const uri = '/api/push/reduxUpdate/statusTransition';
      const apiStruct = {
        firmId: DA.getFirmId(),
        personId: personId,
        transitionId: transitionId,
      };
      const request = createStdRequest(uri, 'POST', apiStruct);
      const actionOnGoodResponse = json => {
        console.log(`response from push API: ${json.success}`);
      };
      apiFetch(this, request, actionOnGoodResponse);
    } else {
    } else {
      const objToSend = {
        kind: 'statusTransition',
        personId: personId,
        transitionId: transitionId,
      };
      SocketAccess.sendReduxUpdateToSocket(objToSend);
    }
  }
  */

  showSnackbar(msg) {
    this.setState({
      snackbar: {
        message: msg + ' saved',
        isOpen: true,
      },
    });
  }

  reduxFieldNamesForClientStateName(clientStateFieldName) {
    switch (clientStateFieldName) {
      case 'phone':
      case 'phoneNumber':
        return ['phoneNumbers'];
      case 'address':
        return ['addresses'];
      case 'name':
        return ['firstName', 'lastName', 'preferredName', 'preferredFullName'];
      default:
        return [clientStateFieldName];
    }
  }

  buildClientApiStructForUpdatingClient() {
    /* expecting:
    public int PersonId { get; set; }
    public string Email { get; set; }
    public string FirstName { get; set; }
    public string MiddleNames { get; set; }
    public string LastName { get; set; }
    public string PreferredName { get; set; }
    public string Sex { get; set; }
    public string BirthDate { get; set; }
    public string LatestContactDate { get; set; }
    public OfficialIdApiStruct OfficialId { get; set; }
    public List<PhoneApiStruct> PhoneNumbers { get; set; } = new List<PhoneApiStruct>();
    public List<AddressApiStruct> Addresses { get; set; } = new List<AddressApiStruct>();

    public int FirmId { get; set; }
    public long ContactDate { get; set; }
    public string Description { get; set; }
    public int OwnerId { get; set; }
  */
    /* to be built from:
  state = {
    loading: true,
    client: {
        personId: 0,
        name: "",
        jobTitle: "",
        description: "",
        phoneNumber: "",
        email: "",
        address: {
          street: "",
          city: "",
          state: "",
          zip: "",
        },
        status: "",
        birthDate: <LuxonDate>,
    },
  */
    const name = this.state.client.name.trim();
    const firstSpaceLoc = name.indexOf(' ');
    const lastSpaceLoc = name.lastIndexOf(' ');
    const firstName = name.substring(0, firstSpaceLoc);
    const lastName = lastSpaceLoc >= firstSpaceLoc ? name.substring(lastSpaceLoc + 1) : '';
    const middleNames = lastSpaceLoc > firstSpaceLoc ? name.substring(firstSpaceLoc + 1, lastSpaceLoc).trim() : '';
    const phoneNumbers = this.createPhonesArrayFromState();
    const addresses = this.createAddressesArrayFromState();
    const birthDate = this.state.client.birthDate;
    const bDayToUse = !birthDate || birthDate.year < 1800 ? defaultBirthdayKludge : birthDate;
    return {
      personId: this.state.client.personId,
      email: this.state.client.email,
      firstName,
      middleNames,
      lastName,
      preferredName: firstName,
      preferredFullName: firstName + ' ' + lastName,
      sex: this.state.client.sex,
      birthDate: bDayToUse.toISO(),
      latestContactDate: '',
      phoneNumbers,
      addresses,
      firmId: DA.getFirmId(),
      contactDate: null,
      description: this.state.client.description.trim(),
      ownerId: this.state.client.ownerId,
      facilityName: this.state.client.facilityName,
    };
  }

  createPhonesArrayFromState() {
    const client = DA.getPersonById(this.state.client.personId);
    if (this.newPhoneObject) {
      if (!this.newPhoneObject.hasOwnProperty('number')) {
        this.newPhoneObject = null;
        if (client.phoneNumbers) {
          if (client.phoneNumbers.length === 0) {
            return [];
          } else {
            return client.phoneNumbers.slice(1);
          }
        } else {
          return [];
        }
      }
      const otherPhones = client.phoneNumbers.slice(1);
      const phonesArray = [this.newPhoneObject, ...otherPhones];
      this.newPhoneObject = null;
      return phonesArray;
    } else {
      return client.phoneNumbers.slice(0);
    }
  }

  createAddressesArrayFromState() {
    const stateAddress = this.state.client.address;
    const line1 = stateAddress.street ? stateAddress.street.trim() : '';
    const city = stateAddress.city ? stateAddress.city.trim() : '';
    const state = stateAddress.state ? stateAddress.state.trim() : '';
    const zip = stateAddress.zip ? stateAddress.zip.trim() : '';
    const address = {
      line1,
      line2: '',
      city,
      state,
      zip,
    };
    return [address];
  }

  updateUI() {
    this.setStateContacts();
  }

  setStateContacts() {
    const contacts = this.getContactsForClient();
    this.setState({ contacts });
  }

  createNoteApiStruct(noteText) {
    const clientId = DA.getCaseIntendedClientId();
    const now = DateTime.local();
    const authorEmail = this.props.userInfo.email;
    const viewingFirmId = DA.getFirmId();
    return {
      date: now.toISO(),
      authorEmail,
      subjectId: clientId,
      viewingFirmId,
      content: noteText,
    };
  }

  createNote(noteText) {
    const uri = '/api/note';
    const apiStruct = this.createNoteApiStruct(noteText);
    const request = createStdRequest(uri, 'POST', apiStruct);
    const actionOnGoodResponse = (json) => {
      // update Redux store
      this.props.clearTaskAccomplishmentInfo();
      const clientId = DA.getCaseIntendedClientId();
      ReduxLoader.addOrUpdateNotesOnPersons([json]);
      this.buildActivityGroupsFromReduxStore(clientId);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  redirectToFirmContact(contact) {
    this.props.setIntendedReferrerContactId(contact.personId);
    this.setState({ redirect: 'referrerContact' });
  }

  redirectToReferral(referral) {
    this.props.setIntendedReferralId(referral.referralId);
    this.setState({ redirect: 'referral' });
  }

  showFirmModalOrDrawer(firm) {
    this.setState({ showCondensedOrgModal: true });
  }

  showVobForm(b) {
    this.setState({ shouldShowVobForm: b });
  }

  getSalesStageOptions() {
    return sortedDropdownItemLabels('Sales Stage');
  }

  getLeadTemperatureOptions() {
    return sortedDropdownItemLabels('Prospect Status');
  }

  getSexOptions() {
    return sortedDropdownItemLabels('Client Gender');
  }

  getEmrFaciltyOptions(shouldShowFacilityChooser) {
    if (!shouldShowFacilityChooser) {
      return null;
    }
    const emrFacilities = DA.getAllEmrFacilities();
    return emrFacilities.map((facility) => facility.name);
  }

  handleSalesStageChange(event) {
    const currentCase = DA.getCurrentCase();
    const newSalesStage = event.target.value;
    const uri = '/api/case/' + currentCase.caseId;
    let apiStruct = this.buildCaseApiStruct(currentCase);
    apiStruct = { ...apiStruct, salesStage: newSalesStage };
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.changeCurrentCaseSalesStage(newSalesStage);
      const prevPerson = DA.getPersonById(currentCase.clientId);
      const updatedPerson = { ...prevPerson, salesStage: newSalesStage };
      this.props.updatePerson(updatedPerson);
      this.setState({ salesStage: newSalesStage });
      this.showSnackbar('Sales stage');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleLeadTemperatureChange(event) {
    const currentCase = DA.getCurrentCase();
    const newTemperature = event.target.value;
    const uri = '/api/case/' + currentCase.caseId;
    let apiStruct = this.buildCaseApiStruct(currentCase);
    apiStruct = { ...apiStruct, leadTemperature: newTemperature };
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.changeCurrentCaseLeadTemperature(newTemperature);
      const prevPerson = DA.getPersonById(currentCase.clientId);
      const updatedPerson = { ...prevPerson, leadTemperature: newTemperature };
      this.props.updatePerson(updatedPerson);
      this.setState({ leadTemperature: newTemperature });
      this.showSnackbar('Lead temperature');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleExpectedAdmissionDateChange(newDate) {
    const currentCase = DA.getCurrentCase();
    const uri = '/api/case/' + currentCase.caseId;
    let apiStruct = this.buildCaseApiStruct(currentCase);
    apiStruct = { ...apiStruct, admissionDate: newDate.toISO() };
    const request = createStdRequest(uri, 'PUT', apiStruct);
    const actionOnGoodResponse = (json) => {
      this.props.changeCurrentCaseAdmissionDate(newDate);
      this.showSnackbar('Expected date of admission');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  buildCaseApiStruct(currentCase) {
    return {
      ...currentCase,
      creationDate: currentCase.creationDate.toISO(),
      admissionDate: currentCase.admissionDate.toISO(),
      dischargeDate: currentCase.dischargeDate.toISO(),
    };
  }

  handleOwnerChange(newOwnerId) {
    const newClientState = { ...this.state.client, ownerId: newOwnerId };
    this.setState({ client: newClientState }, () => this.saveClientOwnerToDb());
  }

  handleAddReferralInfoClick() {
    this.setState({ shouldShowNewReferralDialog: true });
    /*
    if (this.onProductionServer()) {
      alert('This functionality will be available soon');
    } else {
      this.setState({ shouldShowNewReferralDialog: true });
    }
    */
  }

  /*
  onProductionServer() {
    return location.hostname.startsWith('crm.helloalleva');
  }
  */

  saveClientOwnerToDb() {
    this.saveClientStateFieldToDb('ownerId', 'Owner');
  }

  closeNewReferralDialog() {
    this.setState({ shouldShowNewReferralDialog: false });
  }

  render() {
    if (isNonLocalNonLoggedInVisitor()) {
      return <Redirect to="/homeDispatcher" />;
    }

    if (this.state.loading) {
      return <KSpinner />;
    }

    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} push />;
    }

    // console.log(`At top of render(), this.state.client.email: ${this.state.client.email}`);

    const { classes } = this.props;
    const viewingFirmId = DA.getFirmId();
    const clientId = this.state.client.personId;
    const allRelatedTasks = DA.getTasksRelatedToClient(clientId);
    const allNonCanceledRelatedTasks = allRelatedTasks.filter((t) => t.currentState !== 'Canceled');
    const allRelatedDocs = this.simpleGetDocsForClient();
    const haveActivities =
      this.state.activityGroups && this.state.activityGroups[0] && this.state.activityGroups[0].activities[0];
    const lastInteractionTime = haveActivities ? this.state.activityGroups[0].activities[0].dateTime : 0;
    const referral = DA.getReferralOfPersonToFirm(clientId, viewingFirmId);
    const referringOrgId = referral ? referral.referringOrgId : 0;
    const statusOptions = ['Prospect', 'Client', 'Alum'];
    const currentCase = DA.getCurrentCase();
    const salesStageOptions = this.getSalesStageOptions();
    const leadTemperatureOptions = this.getLeadTemperatureOptions();
    const sexOptions = this.getSexOptions();
    const isProspect = this.state.client.status === 'Prospect';
    const phoneNumberRoles = this.state.client.phoneNumbers.length
      ? `(${this.state.client.phoneNumbers[0].roles})`
      : '';
    const {
      name: clientName,
      birthDate,
      address: { street, state, zip, city },
      phoneNumber,
    } = this.state.client;
    const bDayToDisplay = birthDate && !birthDate.equals(defaultBirthdayKludge) ? birthDate : null;
    const name = clientName.trim();
    const firstSpaceLoc = name.indexOf(' ');
    const lastSpaceLoc = name.lastIndexOf(' ');
    const firstName = name.substring(0, firstSpaceLoc);
    const lastName = lastSpaceLoc >= firstSpaceLoc ? name.substring(lastSpaceLoc + 1) : '';

    // Set owner dropdown with name and values
    const possibleOwners = DA.getAllFirmUsers();
    const ownerNames = possibleOwners.map((owner) => owner.name);
    const ownerIds = possibleOwners.map((owner) => owner.personId);

    // get Emr Facilities List
    const shouldShowFacilityChooser = this.props.emrInfo.isSyncedWithFirm;
    const facilities = this.getEmrFaciltyOptions(shouldShowFacilityChooser);

    return (
      <div>
        <AlertModal renderer={this} show={this.state.shouldShowAlertModal} text={this.state.alertModalText} />
        <CrmSnackbar renderer={this} options={this.state.snackbar} />
        <ReferralModal
          show={this.state.shouldShowNewReferralDialog}
          viewingFirmId={viewingFirmId}
          caseId={currentCase.caseId}
          clientId={clientId}
          successFunc={(newReferralId) => alert('No code to handle success yet')}
          closeModalFunc={() => this.closeNewReferralDialog()}
        />
        <VobModal
          show={this.state.shouldShowVobForm}
          clientId={clientId}
          clientInfo={{
            clientFirstName: firstName,
            clientLastName: lastName,
            clientBirthDate: birthDate,
            clientAddressLine1: street,
            clientCity: city,
            clientState: state,
            clientZip: zip,
          }}
          closeModalFunc={() => this.showVobForm(false)}
        />
        {referringOrgId > 0 && (
          <CondensedOrgModal
            show={this.state.showCondensedOrgModal}
            closeModalFunc={() => this.setState({ showCondensedOrgModal: false })}
            firmId={referringOrgId}
          />
        )}
        <Grid container>
          <Grid item xs={12} lg={3}>
            <Grid container className={classes.leftColumn} spacing={2}>
              <Grid item xs={12} className={cx(classes.entityProfileFrame_title, classes.u_ellipsis)}>
                {this.state.client.name}
              </Grid>
              <Grid item xs={12}>
                <KSelect
                  fullWidth
                  liveOptions={statusOptions}
                  value={this.state.client.status}
                  onChange={(event) => this.handleClientStatusChange(event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <ClickAwayTextField
                  label="Name"
                  initialText={this.state.client.name}
                  required
                  outsideClickHandler={(newValue) => this.handleClickOutsideName(newValue)}
                />
              </Grid>
              <Grid item xs={12}>
                <ClickAwayTextField
                  label={`Phone ${phoneNumberRoles}`}
                  initialText={phoneNumber}
                  isPhoneField
                  outsideClickHandler={(newValue) => this.handleClickOutsidePhone(newValue)}
                />
              </Grid>
              <Grid item xs={12}>
                <ClickAwayTextField
                  label="Email"
                  initialText={this.state.client.email}
                  spaceFree
                  outsideClickHandler={(newValue) => this.handleClickOutsideEmail(newValue)}
                />
              </Grid>
              <Grid item xs={12}>
                <ClickAwayTextField
                  label="Description"
                  initialText={this.state.client.description}
                  multiline
                  outsideClickHandler={(newValue) => this.handleClickOutsideDescription(newValue)}
                />
              </Grid>
              <Grid item xs={12}>
                <FacePageUsAddressPanel
                  address={this.state.client.address}
                  validationFunc={(street, city, state, zip) =>
                    this.localValidateAddress(street, city, state, zip).valid
                  }
                  saveFunc={(address) => this.handleAddressChange(address)}
                  changedExternally={this.state.addressWasChangedExternally}
                />
              </Grid>
              <Grid item xs={12}>
                <OwnerDisplay
                  classes={classes}
                  owner={this.state.client.ownerId}
                  options={ownerNames}
                  values={ownerIds}
                  onChange={(event) => this.handleOwnerChange(event.target.value)}
                />
              </Grid>
              {referral ? (
                <Grid item xs={12}>
                  <ReferralDisplay
                    page={this}
                    classes={classes}
                    referral={referral}
                    clientId={this.state.client.personId}
                  />
                </Grid>
              ) : (
                <Grid item xs={12}>
                  <div className={classes.addReferralInfoLink} onClick={() => this.handleAddReferralInfoClick()}>
                    Add Referral Info
                  </div>
                </Grid>
              )}
              <Grid item xs={12}>
                <SalesStageDisplay
                  classes={classes}
                  stage={currentCase.salesStage}
                  options={salesStageOptions}
                  onChange={(event) => this.handleSalesStageChange(event)}
                />
              </Grid>
              {isProspect && (
                <Grid item xs={12}>
                  <LeadTemperatureDisplay
                    classes={classes}
                    temperature={currentCase.leadTemperature}
                    options={leadTemperatureOptions}
                    onChange={(event) => this.handleLeadTemperatureChange(event)}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <GenderChoiceDisplay
                  classes={classes}
                  sex={this.state.client.sex}
                  options={sexOptions}
                  onChange={(event) => this.handleClientSexChange(event.target.value)}
                />
              </Grid>
              {shouldShowFacilityChooser && (
                <Grid item xs={12}>
                  <FacilityDisplay
                    classes={classes}
                    facility={this.state.client.facilityName}
                    options={facilities}
                    disabled={!isProspect}
                    onChange={(event) => this.handleClientEmrFacilityChange(event.target.value)}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <KDatePicker
                  label="Date of Birth"
                  value={bDayToDisplay}
                  onChange={(newDate) => this.handleClientBirthDateChange(newDate)}
                />
              </Grid>
              {isProspect && (
                <Grid item xs={12}>
                  <KDatePicker
                    label="Expected Date of Admission"
                    value={currentCase.admissionDate}
                    onChange={(newDate) => this.handleExpectedAdmissionDateChange(newDate)}
                  />
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid item xs={12} lg={6}>
            <div className={classes.activityColumn}>
              <ActivitySummaryBox
                nInteractions={this.state.nOutreaches}
                lastInteractionTime={lastInteractionTime}
                status={this.state.client.status}
              />

              <ActionPanel
                page={this}
                contacts={this.getClientPlusContacts()}
                client={this.state.client}
                noteCreationFunc={(noteText) => this.createNote(noteText)}
              />

              <ActivityList
                activityGroups={this.state.activityGroups}
                functionToShowSubject={(activity) => this.functionToShowSubject(activity)}
                rerenderPageFunc={() => this.loadRequiredData()}
                page={this}
                contacts={this.getClientPlusContacts()}
                client={this.state.client}
              />
            </div>
          </Grid>
          <Grid item className={classes.rightColumn} xs={12} lg={3}>
            <div className={classes.summonVobFormDiv}>
              <CrmButton filled size="small" onClick={() => this.showVobForm(true)}>
                Show VOB Info
              </CrmButton>
            </div>
            <ContactAssocGroup
              contactsFunc={() => this.getContactsForClient()}
              viewingFirmId={viewingFirmId}
              clientId={clientId}
              updateFunc={() => this.updateUI()}
              contactType="client"
            />
            {false && <NoteAssocGroup notes={this.getNotesAboutClient(clientId)} />}
            <TaskAssocGroup
              initialSubjectId={clientId}
              initialSubjectStatus={this.state.client.status}
              taskSubjectProvider={this}
              allRelevantTasks={allNonCanceledRelatedTasks}
              refreshFunc={() => this.setState({ loading: false })}
            />
            <DocsAssocGroup
              docsSubjectProvider={this}
              allRelevantDocs={allRelatedDocs}
              clientId={clientId}
              inquiryId={0}
              parentState={this}
            />
          </Grid>
        </Grid>
      </div>
    );
  }
}

CasePage.propTypes = {
  classes: PropTypes.object.isRequired,
};

const computeReferredByString = (referral) => {
  if (referral.referringPersonId) {
    return 'Referred by';
  }
  if (referral.sourceType) {
    const bareSource = `Referred by ${referral.sourceType}`;
    if (referral.sourceSpecies) {
      return `${bareSource} - ${referral.sourceSpecies}`;
    } else {
      return bareSource;
    }
  } else {
    return 'Referred by Self';
  }
};

const ReferralDisplay = (props) => {
  const { page, classes, referral, clientId } = props;
  const haveReferrer = referral.referringPersonId > 0;
  const referrer = haveReferrer ? DA.getPersonById(referral.referringPersonId) : DA.getPersonById(clientId);
  const phoneNumber = referrer.phoneNumbers.length > 0 ? referrer.phoneNumbers[0].number : '';
  const referrerName = referrer ? referrer.preferredFullName : '';
  const company = referral.referringOrgId ? DA.getFirmById(referral.referringOrgId) : null;
  const companyName = company ? company.name : '';
  if (!referrerName && !companyName) {
    return null;
  }
  const clickableSpanClasses = cx(classes.entityProfileFrame_subtitleName, classes.u_cursorPointer);
  const referredByClasses = classes.u_cursorPointer;
  const referredByStr = computeReferredByString(referral);
  return (
    <div>
      <div className={referredByClasses} onClick={() => page.redirectToReferral(referral)}>
        {referredByStr}
      </div>
      {haveReferrer && (
        <div className={classes.referrerDiv}>
          <div>
            {referrerName && companyName ? (
              <div>
                <span className={clickableSpanClasses} onClick={() => page.redirectToFirmContact(referrer)}>
                  {referrerName}
                </span>
                {' at '}
                <span className={clickableSpanClasses} onClick={() => page.showFirmModalOrDrawer(company)}>
                  {companyName}
                </span>
              </div>
            ) : referrerName ? (
              <span className={classes.u_cursorPointer} onClick={() => page.redirectToFirmContact(referrer)}>
                {referrerName}
              </span>
            ) : (
              <span className={classes.u_cursorPointer} onClick={() => page.showFirmModalOrDrawer(company)}>
                {companyName}
              </span>
            )}
          </div>
          <div>
            {phoneNumber && referrer.email ? (
              <div>
                <span className={classes.entityProfileFrame_subtitleName}>{phoneNumber}</span>
                {' | '}
                <span className={classes.entityProfileFrame_subtitleName}>{referrer.email}</span>
              </div>
            ) : phoneNumber ? (
              <span className={classes.u_cursorPointer}>
                <strong>{phoneNumber}</strong>
              </span>
            ) : (
              <span className={classes.u_cursorPointer}>
                <strong>{referrer.email}</strong>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const SalesStageDisplay = ({ classes, stage, options, onChange }) => {
  const actualStage = stage ? stage : 'Initial Contact';
  return (
    <div>
      <div>Sales Stage</div>
      <div className={classes.labeledDropdown}>
        <KSelect
          fullWidth={true}
          disabledOption="Choose Stage"
          liveOptions={options}
          value={actualStage}
          onChange={(event) => onChange(event)}
        />
      </div>
    </div>
  );
};

const LeadTemperatureDisplay = ({ classes, temperature, options, onChange }) => {
  const actualTemperature = temperature ? temperature : 'Warm';
  return (
    <div>
      <div>Lead Temperature</div>
      <div className={classes.labeledDropdown}>
        <KSelect
          fullWidth={true}
          disabledOption="Choose Temperature"
          liveOptions={options}
          value={actualTemperature}
          onChange={(event) => onChange(event)}
        />
      </div>
    </div>
  );
};

const GenderChoiceDisplay = ({ classes, sex, options, onChange }) => {
  const specifiedSex = sex ? sex : '';
  return (
    <div>
      <div>Gender</div>
      <div className={classes.labeledDropdown}>
        <KSelect
          fullWidth={true}
          disabledOption="Choose Gender"
          liveOptions={options}
          value={specifiedSex}
          onChange={(event) => onChange(event)}
        />
      </div>
    </div>
  );
};

const OwnerDisplay = ({ classes, owner, options, values, onChange }) => {
  const specifiedOwner = owner ? owner : 'Choose Owner';
  return (
    <div>
      <div>Owner</div>
      <div className={classes.labeledDropdown}>
        <KSelect
          fullWidth={true}
          disabledOption="Choose Owner"
          liveOptions={options}
          liveValues={values}
          value={specifiedOwner}
          onChange={(event) => onChange(event)}
        />
      </div>
    </div>
  );
};

const FacilityDisplay = ({ classes, facility, options, disabled, onChange }) => {
  const specifiedfacility = facility ? facility : '';
  return (
    <div>
      <div>Facility</div>
      <div className={classes.labeledDropdown}>
        <KSelect
          fullWidth={true}
          disabled={disabled}
          disabledOption="Choose Facility"
          liveOptions={options}
          value={specifiedfacility}
          onChange={(event) => onChange(event)}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
  firmId: state.firmInfo.firmId,
  emrInfo: state.emrInfo,
  taskInfo: state.taskInfo,
  docs: state.documents,
  firms: state.firms,
  clientId: state.intendedInfo.intendedReferrerCompanyId,
});

const actionProducers = {
  updatePerson,
  addStatusTransitions,
  updateCase,
  changeCurrentCaseLeadTemperature,
  changeCurrentCaseSalesStage,
  changeCurrentCaseAdmissionDate,
  setIntendedReferrerContactId,
  setIntendedReferralId,
  clearTaskAccomplishmentInfo,
  setApiResponseInfo,
  addEmrClient,
  setEmrClientFacility,
};

export default connect(mapStateToProps, actionProducers)(withStyles(casePageStyles)(CasePage));
